import React, { ReactNode } from "react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverContentProps,
  PopoverBody,
  PopoverCloseButton,
  PopoverProps,
  IconButton,
  IconButtonProps,
} from "@chakra-ui/react";
import { QuestionMarkIcon } from "@root/assets/icons/questionMarkIcon";

export interface HelpPopoverProps
  extends Omit<PopoverProps, "children" | "trigger"> {
  /**
   * Content to be displayed inside the popover
   */
  children: ReactNode;
  /**
   * Custom trigger element. If not provided, defaults to QuestionMarkIcon button
   */
  trigger?: ReactNode;
  /**
   * Props to pass to the default IconButton trigger (only used when trigger is not provided)
   */
  triggerButtonProps?: IconButtonProps;
  /**
   * Width of the popover content
   * @default "600px"
   */
  contentWidth?: string;
  /**
   * Props to pass to PopoverContent (excluding width which is controlled by contentWidth)
   */
  contentProps?: Omit<PopoverContentProps, "w" | "children">;
  /**
   * Whether to show the close button
   * @default true
   */
  showCloseButton?: boolean;
}

/**
 * Reusable Help Popover component
 * Displays a popover with customizable content, typically triggered by a question mark icon
 *
 * @example
 * ```tsx
 * <HelpPopover
 *   title="Help Title"
 *   contentWidth="800px"
 * >
 *   <Text>Your help content here</Text>
 * </HelpPopover>
 * ```
 */
export const HelpPopover: React.FC<HelpPopoverProps> = ({
  children,
  trigger,
  triggerButtonProps,
  contentWidth = "700px",
  contentProps,
  showCloseButton = true,
  placement = "right-start",
  closeOnBlur = true,
  ...popoverProps
}) => {
  const defaultTrigger = (
    <IconButton
      aria-label="Help"
      icon={<QuestionMarkIcon />}
      variant="ghost"
      size="sm"
      minW="20px"
      w="20px"
      h="20px"
      p={0}
      _hover={{ bg: "transparent", opacity: 0.7 }}
      {...triggerButtonProps}
    />
  );

  return (
    <Popover placement={placement} closeOnBlur={closeOnBlur} {...popoverProps}>
      <PopoverTrigger>{trigger || defaultTrigger}</PopoverTrigger>
      <PopoverContent
        w={contentWidth}
        borderRadius="8px"
        bg="white"
        boxShadow="0px 4px 20px rgba(0, 0, 0, 0.15)"
        border="none"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        style={{ zIndex: 1500, position: "relative" }}
        {...(contentProps as any)}
      >
        {showCloseButton && <PopoverCloseButton />}
        <PopoverBody p={6}>{children}</PopoverBody>
      </PopoverContent>
    </Popover>
  );
};
